import { useFetchGet } from '@/composables/useFetch'
import { Page, getTicketTitles } from '@/services/tickets.service'
import { computed, ref, watch } from 'vue'
import { Ticket } from './Contracts'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { calculateTicketListQuery } from '@/fetches'

export type Pageable = {
  size: number
  page: number
}

const PAGE_SIZE = 10

export const useFetchTickets = () => {
  const filters = useFilters(FeatureType.TicketAnalysis)
  const items = ref<Record<number, Ticket[]>>([])
  const titleMap = ref<Record<string, string>>({})
  const page = ref<number>(0)
  const totalTickets = ref<number>(0)

  const query = computed(() => calculateTicketListQuery(filters.submitted))

  const urlWithParams = computed(() => {
    const params = encodeURIComponent(JSON.stringify(query.value))

    return `/iot/v1/tickets/list?page=${page.value}&size=${PAGE_SIZE}&query=${params}`
  })

  const itemList = computed(() => {
    return Object.entries(items.value)
      .map(([a, b]) => [parseInt(a), b] as const)
      .toSorted((a, b) => a[0] - b[0])
      .map(([, value]) => value)
      .flat()
  })

  const { data } = useFetchGet<Page<Ticket>>(urlWithParams)

  const maxPage = computed(() => data.value?.totalPages ?? 0)

  watch(data, async (newData) => {
    if (newData) {
      const tickets = newData.content
      fetchTitles(tickets)
      items.value[page.value] = tickets
      totalTickets.value = newData.totalElements
    }
  })

  watch(query, () => {
    clear()
  })

  async function fetchTitles(tickets: Ticket[]) {
    const ids = tickets.map((ticket) => ticket.ticketId)
    const titles = (await getTicketTitles(ids))?.titles
    if (!titles) return
    titleMap.value = {
      ...titleMap.value,
      ...Object.fromEntries(titles.map((title) => [title.id, title.title]))
    }
  }

  function loadMore() {
    if (page.value >= maxPage.value) return
    page.value++
  }

  function clear() {
    page.value = 0
    items.value = {}
    titleMap.value = {}
  }

  return {
    loadMore,
    clear,
    itemList,
    titleMap,
    totalTickets
  }
}
